/**
 * @fileoverview Formulário de WACC por Empresa (Sétima vs 555 Studios)
 * 
 * @module WACCPorEmpresaForm
 * @description
 * Permite editar WACC separadamente para cada empresa, refletindo perfis de risco distintos:
 * - **Sétima:** Empresa estabelecida (beta ~1.0-1.2)
 * - **555 Studios:** Startup hypergrowth (beta ~1.4-1.8)
 * 
 * @features
 * - WACC calculado separadamente via CAPM
 * - Beta editável por empresa
 * - WACC Mix ponderado (por receita)
 * - Validações em tempo real
 * - Tooltips educativos
 */

import React, { useMemo, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Divider,
  Stack,
  Chip,
  useTheme,
} from '@mui/material';
import { NumberInput, PercentageInput } from './CommonInputs';
import { formatPercentage, formatNumber } from '../../utils/formatters';
import {
  IconInfo,
  IconDollarSign,
  IconDashboard,
  IconLineChart,
} from '../../utils/icons';
import { AlertTriangle, Building2, Info, CheckCircle } from 'lucide-react';
import { validateBeta } from '../../hooks/useValuationValidations';
import { GlossarioTooltip } from '../glossario/GlossarioTooltip';
import { useGlossarioModal } from '../../hooks/useGlossarioModal';

// ============================================================================
// TYPES
// ============================================================================

export interface WACCEmpresa {
  beta: number;
  wacc?: number; // Calculado automaticamente
}

export interface WACCPorEmpresaData {
  // Componentes compartilhados (macroeconômicos)
  taxaLivreRisco: number; // Rf - Selic
  premioRiscoMercado: number; // Rm - Rf
  premioRiscoBrasil: number; // EMBI+
  
  // Específicos por empresa
  setima: WACCEmpresa;
  studios555: WACCEmpresa;
  
  // Estrutura de capital (simplificado - apenas equity)
  debtToEquity: number; // D/E ratio (padrão 0)
  taxaImposto: number; // Alíquota IR/CSLL
}

interface WACCPorEmpresaFormProps {
  values: WACCPorEmpresaData;
  onChange: (values: WACCPorEmpresaData) => void;
  
  // Receitas para cálculo do WACC Mix
  receitaSetima?: number;
  receita555?: number;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Calcula Ke (custo do equity) via CAPM
 * Ke = Rf + β × (Rm - Rf) + Risco País
 */
const calcularKe = (
  rf: number,
  beta: number,
  rmRf: number,
  riscoBrasil: number
): number => {
  return rf + beta * rmRf + riscoBrasil;
};

/**
 * Calcula WACC (simplificado - 100% equity)
 * WACC = Ke (sem dívida)
 */
const calcularWACC = (ke: number): number => {
  return ke;
};

/**
 * Calcula WACC Mix ponderado por receita
 */
const calcularWACCMix = (
  waccSetima: number,
  wacc555: number,
  receitaSetima: number,
  receita555: number
): { mix: number; pesoSetima: number; peso555: number } => {
  const receitaTotal = receitaSetima + receita555;
  const pesoSetima = receitaSetima / receitaTotal;
  const peso555 = receita555 / receitaTotal;
  const mix = waccSetima * pesoSetima + wacc555 * peso555;
  
  return { mix, pesoSetima, peso555 };
};

// ============================================================================
// COMPONENT
// ============================================================================

export const WACCPorEmpresaForm: React.FC<WACCPorEmpresaFormProps> = ({
  values,
  onChange,
  receitaSetima = 8_500_000,
  receita555 = 5_000_000,
}) => {
  const theme = useTheme();
  const { abrir: abrirGlossario } = useGlossarioModal();
  
  // Calcular WACCs
  const keSetima = useMemo(
    () =>
      calcularKe(
        values.taxaLivreRisco,
        values.setima.beta,
        values.premioRiscoMercado,
        values.premioRiscoBrasil
      ),
    [values.taxaLivreRisco, values.setima.beta, values.premioRiscoMercado, values.premioRiscoBrasil]
  );

  const ke555 = useMemo(
    () =>
      calcularKe(
        values.taxaLivreRisco,
        values.studios555.beta,
        values.premioRiscoMercado,
        values.premioRiscoBrasil
      ),
    [values.taxaLivreRisco, values.studios555.beta, values.premioRiscoMercado, values.premioRiscoBrasil]
  );

  const waccSetima = useMemo(() => calcularWACC(keSetima), [keSetima]);
  const wacc555 = useMemo(() => calcularWACC(ke555), [ke555]);

  const waccMixData = useMemo(
    () => calcularWACCMix(waccSetima, wacc555, receitaSetima, receita555),
    [waccSetima, wacc555, receitaSetima, receita555]
  );

  // 🔍 DEBUG: Log quando Ke recalcula
  useEffect(() => {
    console.log('🔄 WACCPorEmpresaForm - Ke recalculado:', {
      betaSetima: values.setima.beta,
      keSetima: keSetima.toFixed(4),
      ke555: ke555.toFixed(4),
    });
  }, [keSetima, ke555, values.setima.beta, values.studios555.beta]);

  // Validações de Beta
  const [betaSetimaValidation, setBetaSetimaValidation] = useState<ReturnType<typeof validateBeta> | null>(null);
  const [beta555Validation, setBeta555Validation] = useState<ReturnType<typeof validateBeta> | null>(null);

  // Handlers
  const handleBetaSetimaChange = (beta: number) => {
    // Validar Beta
    const validation = validateBeta(beta);
    setBetaSetimaValidation(validation);
    
    // ✅ NÃO passar wacc aqui - será recalculado automaticamente pelo useMemo
    onChange({
      ...values,
      setima: { ...values.setima, beta },
    });
  };

  const handleBeta555Change = (beta: number) => {
    // Validar Beta
    const validation = validateBeta(beta);
    setBeta555Validation(validation);
    
    // ✅ NÃO passar wacc aqui - será recalculado automaticamente pelo useMemo
    onChange({
      ...values,
      studios555: { ...values.studios555, beta },
    });
  };

  const handleSharedChange = (field: keyof WACCPorEmpresaData, value: number) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconDollarSign size={24} />
        WACC por Empresa (Custo Médio Ponderado de Capital)
      </Typography>

      <Alert severity="info" icon={<IconInfo size={20} />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AlertTriangle size={16} />
          <strong>IMPORTANTE:</strong> Sétima e 555 Studios têm <strong>perfis de risco diferentes</strong>.
          O WACC é calculado separadamente para refletir essas diferenças.
        </Typography>
      </Alert>

      {/* Componentes Compartilhados (Macroeconômicos) */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconDashboard size={18} />
          Componentes Macroeconômicos (Compartilhados)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <PercentageInput
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Rf - Taxa Livre de Risco
                  <GlossarioTooltip
                    termo="WACC"
                    definicao="Taxa de retorno de um ativo sem risco (geralmente Selic)"
                    onClick={() => abrirGlossario('WACC')}
                  />
                </Box>
              }
              value={values.taxaLivreRisco}
              onChange={(value) => handleSharedChange('taxaLivreRisco', value)}
              asDecimal={true}
              min={0}
              max={50}
              step={0.25}
              helpText="Taxa Selic atual (risco zero)"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <PercentageInput
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Prêmio Risco Mercado
                  <GlossarioTooltip
                    termo="WACC"
                    definicao="Retorno extra esperado do mercado acima da taxa livre de risco (Rm - Rf)"
                    onClick={() => abrirGlossario('WACC')}
                  />
                </Box>
              }
              value={values.premioRiscoMercado}
              onChange={(value) => handleSharedChange('premioRiscoMercado', value)}
              asDecimal={true}
              min={0}
              max={50}
              step={0.25}
              helpText="Rm - Rf (retorno esperado acima da Selic)"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <PercentageInput
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Prêmio Risco Brasil
                  <GlossarioTooltip
                    termo="WACC"
                    definicao="Spread de risco adicional para investimentos no Brasil (EMBI+)"
                    onClick={() => abrirGlossario('WACC')}
                  />
                </Box>
              }
              value={values.premioRiscoBrasil}
              onChange={(value) => handleSharedChange('premioRiscoBrasil', value)}
              asDecimal={true}
              min={0}
              max={20}
              step={0.25}
              helpText="EMBI+ Brasil (risco país)"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* WACC Sétima */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Building2 size={18} /> 
            WACC - Sétima
            <GlossarioTooltip
              termo="WACC"
              definicao="Weighted Average Cost of Capital - Custo médio ponderado de capital"
              onClick={() => abrirGlossario('WACC')}
            />
          </Typography>
          <Chip label="Estabelecida" color="primary" size="small" />
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong>Perfil:</strong> Empresa estabelecida (2020+), crescimento moderado (6-8% CAGR), 
            risco médio. Beta típico: 1.0 - 1.2
          </Typography>
        </Alert>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <NumberInput
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Beta (β) - Sétima
                  <GlossarioTooltip
                    termo="BETA"
                    definicao="Medida de risco sistemático da ação em relação ao mercado"
                    onClick={() => abrirGlossario('BETA')}
                  />
                </Box>
              }
              value={values.setima.beta}
              onChange={handleBetaSetimaChange}
              min={0.1}
              max={5.0}
              step={0.05}
              helpText="Range sugerido: 1.0 - 1.2 (empresas estabelecidas). Beta >2.0 indica alta volatilidade"
            />
            {/* Validação de Beta - Sétima */}
            {betaSetimaValidation && !betaSetimaValidation.isValid && betaSetimaValidation.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {betaSetimaValidation.error}
              </Alert>
            )}
            {betaSetimaValidation && betaSetimaValidation.isValid && betaSetimaValidation.warning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {betaSetimaValidation.warning}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(100, 181, 246, 0.08)'  // Azul translúcido no dark
                : 'rgba(33, 150, 243, 0.08)',  // Azul translúcido no light
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(100, 181, 246, 0.3)'
                : 'rgba(33, 150, 243, 0.3)',
            }}>
              <Typography 
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                Ke Calculado (CAPM):
              </Typography>
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(100, 181, 246, 1)'
                    : 'rgba(33, 150, 243, 1)',
                }}
              >
                {formatPercentage(keSetima * 100, 2)}%
              </Typography>
              <Typography 
                variant="caption" 
                display="block" 
                sx={{ 
                  mt: 0.5,
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                = {formatPercentage(values.taxaLivreRisco * 100, 2)}% + {formatNumber(values.setima.beta, 2)} × {formatPercentage(values.premioRiscoMercado * 100, 2)}% + {formatPercentage(values.premioRiscoBrasil * 100, 2)}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'success.lighter', border: '2px solid', borderColor: 'success.main' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                WACC Final - Sétima
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.dark">
                {formatPercentage(waccSetima * 100, 2)}%
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                (100% equity, sem dívida)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* WACC 555 Studios */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            🎬 WACC - 555 Studios
            <GlossarioTooltip
              termo="WACC"
              definicao="Weighted Average Cost of Capital - Custo médio ponderado de capital"
              onClick={() => abrirGlossario('WACC')}
            />
          </Typography>
          <Chip label="Startup Hypergrowth" color="secondary" size="small" />
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong>Perfil:</strong> Startup early-stage (2023+), hypergrowth (180% CAGR), 
            alta volatilidade. Beta típico: 1.4 - 1.8
          </Typography>
        </Alert>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <NumberInput
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Beta (β) - 555 Studios
                  <GlossarioTooltip
                    termo="BETA"
                    definicao="Medida de risco sistemático da ação em relação ao mercado"
                    onClick={() => abrirGlossario('BETA')}
                  />
                </Box>
              }
              value={values.studios555.beta}
              onChange={handleBeta555Change}
              min={0.1}
              max={5.0}
              step={0.05}
              helpText="Range sugerido: 1.4 - 1.8 (empresas growth). Beta >2.0 indica alta volatilidade"
            />
            {/* Validação de Beta - 555 Studios */}
            {beta555Validation && !beta555Validation.isValid && beta555Validation.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {beta555Validation.error}
              </Alert>
            )}
            {beta555Validation && beta555Validation.isValid && beta555Validation.warning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {beta555Validation.warning}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(206, 147, 216, 0.08)'  // Roxo translúcido no dark
                : 'rgba(156, 39, 176, 0.08)',  // Roxo translúcido no light
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(206, 147, 216, 0.3)'
                : 'rgba(156, 39, 176, 0.3)',
            }}>
              <Typography 
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                Ke Calculado (CAPM):
              </Typography>
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(206, 147, 216, 1)'
                    : 'rgba(156, 39, 176, 1)',
                }}
              >
                {(ke555 * 100).toFixed(2)}%
              </Typography>
              <Typography 
                variant="caption" 
                display="block" 
                sx={{ 
                  mt: 0.5,
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                = {(values.taxaLivreRisco * 100).toFixed(2)}% + {values.studios555.beta.toFixed(2)} × {(values.premioRiscoMercado * 100).toFixed(2)}% + {(values.premioRiscoBrasil * 100).toFixed(2)}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255, 183, 77, 0.08)'  // Laranja translúcido no dark
                : 'rgba(255, 152, 0, 0.08)',  // Laranja translúcido no light
              border: '2px solid',
              borderColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 183, 77, 0.5)'
                : 'rgba(255, 152, 0, 0.5)',
            }}>
              <Typography 
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                <strong>WACC Final - 555 Studios:</strong>
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight="bold"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 183, 77, 1)'
                    : 'rgba(255, 152, 0, 1)',
                }}
              >
                {(wacc555 * 100).toFixed(2)}%
              </Typography>
              <Typography 
                variant="caption" 
                display="block" 
                sx={{ 
                  mt: 0.5,
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                (100% equity, sem dívida)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* WACC Mix Consolidado */}
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconDashboard size={18} />
          WACC Mix Consolidado (Ponderado por Receita)
        </Typography>

        <Paper sx={{ 
          p: 2, 
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(144, 202, 249, 0.08)'  // Azul translúcido no dark
            : 'rgba(33, 150, 243, 0.08)',  // Azul translúcido no light
          border: '2px solid',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(144, 202, 249, 0.5)'
            : 'rgba(33, 150, 243, 0.5)',
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                Sétima: {(waccSetima * 100).toFixed(2)}% × {(waccMixData.pesoSetima * 100).toFixed(1)}% (peso)
              </Typography>
              <Typography 
                variant="caption" 
                display="block"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                555: {(wacc555 * 100).toFixed(2)}% × {(waccMixData.peso555 * 100).toFixed(1)}% (peso)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="caption"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : 'rgba(0, 0, 0, 0.7)',
                }}
              >
                <strong>WACC Mix Final:</strong>
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight="bold"
                sx={{
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(144, 202, 249, 1)'
                    : 'rgba(33, 150, 243, 1)',
                }}
              >
                {(waccMixData.mix * 100).toFixed(2)}%
              </Typography>
            </Grid>
          </Grid>

          <Alert severity="info" icon={<IconLineChart size={20} />} sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Info size={14} />
              <strong>Nota:</strong> O WACC Mix é usado apenas para referência. 
              Cada empresa é avaliada com seu WACC específico no modelo DCF.
            </Typography>
          </Alert>
        </Paper>
      </Box>

      {/* Validações */}
      <Box mt={3}>
        <Stack spacing={1}>
          {waccSetima < wacc555 && (
            <Alert severity="success" icon={<CheckCircle size={20} />}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircle size={16} />
                <strong>Hierarquia correta:</strong> WACC Sétima ({(waccSetima * 100).toFixed(2)}%) {'<'} 
                WACC 555 ({(wacc555 * 100).toFixed(2)}%), refletindo perfis de risco distintos.
              </Typography>
            </Alert>
          )}
          {waccSetima >= wacc555 && (
            <Alert severity="warning" icon={<AlertTriangle size={20} />}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AlertTriangle size={16} />
                <strong>Atenção:</strong> WACC Sétima ({(waccSetima * 100).toFixed(2)}%) ≥ 
                WACC 555 ({(wacc555 * 100).toFixed(2)}%). Esperado: Startup tem maior risco que empresa estabelecida.
              </Typography>
            </Alert>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default WACCPorEmpresaForm;
